import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Tag, Search, Home, KeyRound,
  Clock, Compass, Building, Plane,
  TrendingUp, Zap, HandHelping, Globe, Lock,
  Wallet, Calendar, MapPin, Users, Crown,
  Briefcase, ShieldCheck, Map
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import BrokerApplicationModal from '../components/BrokerApplicationModal';
import './FindAgentPage.css';

const LOOKING_FOR_OPTIONS = [
  { value: 'sell',     tKey: 'sell',     Icon: Tag },
  { value: 'buy',      tKey: 'buy',      Icon: Search },
  { value: 'rent',     tKey: 'rent',     Icon: Home },
  { value: 'rent_out', tKey: 'rent_out', Icon: KeyRound },
];

const PROPERTY_TYPES = [
  { value: 'apartment',  tKey: 'apartment'  },
  { value: 'house',      tKey: 'house'      },
  { value: 'villa',      tKey: 'villa'      },
  { value: 'land',       tKey: 'land'       },
  { value: 'commercial', tKey: 'commercial' },
];

function PropertyIcon({ type, size = 20 }) {
  if (type === 'land')       return <Map size={size} strokeWidth={1.7} />;
  if (type === 'commercial') return <Briefcase size={size} strokeWidth={1.7} />;
  const icons = {
    apartment: <><rect x="3" y="2" width="18" height="20" rx="1"/><line x1="9" y1="6" x2="9" y2="6.01"/><line x1="12" y1="6" x2="12" y2="6.01"/><line x1="15" y1="6" x2="15" y2="6.01"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="12" y1="10" x2="12" y2="10.01"/><line x1="15" y1="10" x2="15" y2="10.01"/><path d="M9 22v-4h6v4"/></>,
    house:     <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    villa:     <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><rect x="7" y="14" width="4" height="8"/><rect x="13" y="14" width="4" height="8"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{icons[type] || icons.house}</svg>;
}

const CITIES = ['Limassol', 'Paphos', 'Nicosia', 'Larnaca', 'Ayia Napa', 'Protaras', 'Paralimni'];
const BEDROOMS = ['1', '2', '3', '4', '5+'];

const SITUATIONS = {
  sell: [
    { value: 'specific_timeline', Icon: Clock },
    { value: 'exploring',         Icon: Compass },
    { value: 'inheritance',       Icon: Building },
    { value: 'relocate',          Icon: Plane },
  ],
  buy: [
    { value: 'first_time',           Icon: Home },
    { value: 'investor',             Icon: TrendingUp },
    { value: 'relocating_to_cyprus', Icon: Plane },
    { value: 'golden_visa',          Icon: Crown },
  ],
  rent: [
    { value: 'short_term',           Icon: Calendar },
    { value: 'long_term',            Icon: Home },
    { value: 'relocating_to_cyprus', Icon: Plane },
    { value: 'students_or_family',   Icon: Users },
  ],
  rent_out: [
    { value: 'short_term_rental',    Icon: Calendar },
    { value: 'long_term_rental',     Icon: Users },
    { value: 'need_management',      Icon: Briefcase },
    { value: 'just_tenant_matching', Icon: HandHelping },
  ],
};

const TIMEFRAMES_SELL = ['immediate', 'within_3_months', 'within_6_months', 'within_12_months', 'within_24_months', 'just_exploring'];
const MOVE_IN_OPTIONS = ['asap', 'within_6_months', 'within_12_months', 'within_24_months', 'flexible'];

const PRIORITIES = {
  sell: [
    { value: 'best_price',   Icon: TrendingUp },
    { value: 'fast_sale',    Icon: Zap },
    { value: 'full_process', Icon: HandHelping },
    { value: 'language',     Icon: Globe },
    { value: 'privacy',      Icon: Lock },
  ],
  buy: [
    { value: 'best_location', Icon: MapPin },
    { value: 'best_value',    Icon: Wallet },
    { value: 'exclusive',     Icon: ShieldCheck },
    { value: 'language',      Icon: Globe },
    { value: 'full_process',  Icon: HandHelping },
  ],
  rent: [
    { value: 'best_location',   Icon: MapPin },
    { value: 'best_value',      Icon: Wallet },
    { value: 'fast_response',   Icon: Zap },
    { value: 'language',        Icon: Globe },
    { value: 'furnished_ready', Icon: Home },
  ],
  rent_out: [
    { value: 'vetted_tenants',  Icon: ShieldCheck },
    { value: 'max_yield',       Icon: TrendingUp },
    { value: 'full_management', Icon: HandHelping },
    { value: 'language',        Icon: Globe },
    { value: 'fast_fill',       Icon: Zap },
  ],
};

const CONTACT_METHODS = ['email', 'call', 'whatsapp'];

const TOTAL_STEPS = 5;

export default function FindAgentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  // Deep-link: /find-agent?apply=1 auto-opens the broker application modal.
  // Used by the About page agents CTA so users land directly in the apply form.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('apply') === '1') setShowApplyModal(true);
  }, [location.search]);

  const [formData, setFormData] = useState({
    lookingFor: '',
    propertyType: '',
    city: '',
    district: '',
    bedrooms: '',
    budgetMin: '',
    budgetMax: '',
    situation: '',
    sellingTimeframe: '',
    moveInTiming: '',
    priority: '',
    fullName: '',
    email: '',
    phone: '',
    preferredContactMethod: 'email',
    consent: false,
  });

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const isSellerOrLandlord = formData.lookingFor === 'sell' || formData.lookingFor === 'rent_out';
  const isBuyerOrRenter = formData.lookingFor === 'buy' || formData.lookingFor === 'rent';
  const showBedrooms = formData.propertyType && formData.propertyType !== 'land' && formData.propertyType !== 'commercial';
  const showBudget = isBuyerOrRenter;

  const canProceed = () => {
    if (step === 1) return formData.lookingFor;
    if (step === 2) return formData.city && (formData.propertyType || formData.lookingFor === 'land');
    if (step === 3) {
      if (isSellerOrLandlord) return formData.situation && formData.sellingTimeframe;
      return formData.situation && formData.moveInTiming;
    }
    if (step === 4) return formData.priority;
    if (step === 5) return formData.fullName && formData.email && formData.consent;
    return false;
  };

  const handleNext = () => { if (step < TOTAL_STEPS) setStep(step + 1); };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    // Canonical EN string is persisted in DB for audit-trail consistency,
    // regardless of the language the user saw on screen.
    const consentText =
      'I allow my contact details to be shared with up to 3 vetted real estate agents who match my requirements';

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const payload = {
        user_id: user?.id || null,
        looking_for: formData.lookingFor,
        property_type: formData.propertyType || null,
        city: formData.city,
        district: formData.district || null,
        bedrooms: formData.bedrooms || null,
        budget_min: formData.budgetMin ? parseInt(formData.budgetMin, 10) : null,
        budget_max: formData.budgetMax ? parseInt(formData.budgetMax, 10) : null,
        situation: formData.situation,
        selling_timeframe: isSellerOrLandlord ? formData.sellingTimeframe : null,
        move_in_timing: isBuyerOrRenter ? formData.moveInTiming : null,
        priority: formData.priority,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        preferred_contact_method: formData.preferredContactMethod,
        preferred_contact_time: null,
        consent_to_share: true,
        consent_text: consentText,
        consent_timestamp: new Date().toISOString(),
      };

      const { data, error: insertError } = await supabase
        .from('agent_matching_leads')
        .insert(payload)
        .select()
        .single();

      if (insertError) throw insertError;

      fetch('/api/notify-new-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: data.id })
      }).catch(err => console.error('Notification failed:', err));

      navigate('/find-agent/success', { state: { leadId: data.id, city: formData.city } });
    } catch (err) {
      console.error('Submission error:', err);
      setError(t('findAgent.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const currentSituations = SITUATIONS[formData.lookingFor] || [];
  const currentPriorities = PRIORITIES[formData.lookingFor] || [];

  return (
    <div className="fa-page">
      <div className="fa-container">
        <div className="fa-progress">
          <div className="fa-step-indicator">
            {t('findAgent.stepIndicator').replace('{step}', step).replace('{total}', TOTAL_STEPS)}
          </div>
          <div className="fa-progress-bar">
            <div className="fa-progress-fill" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
          </div>
        </div>

        {step === 1 && (
          <div className="fa-step">
            <h1 className="fa-headline">{t('findAgent.step1Title')}</h1>
            <p className="fa-subline">{t('findAgent.step1Sub')}</p>
            <div className="fa-card-grid fa-card-grid--2col">
              {LOOKING_FOR_OPTIONS.map(opt => {
                const Icon = opt.Icon;
                return (
                  <div
                    key={opt.value}
                    className={`fa-option-card ${formData.lookingFor === opt.value ? 'fa-option-card--selected' : ''}`}
                    onClick={() => updateField('lookingFor', opt.value)}
                  >
                    <div className="fa-icon-wrapper">
                      <Icon size={22} />
                    </div>
                    <div className="fa-option-text">
                      <div className="fa-option-label">{t('findAgent.lookingFor.' + opt.tKey)}</div>
                      <div className="fa-option-subtitle">{t('findAgent.lookingFor.' + opt.tKey + 'Sub')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="fa-step">
            <h1 className="fa-headline">{t('findAgent.step2Title')}</h1>
            <p className="fa-subline">{t('findAgent.step2Sub')}</p>

            {formData.lookingFor !== 'land' && (
              <div className="fa-section">
                <label className="fa-label">{t('findAgent.propertyTypeLabel')}</label>
                <div className="fa-card-grid fa-card-grid--5col">
                  {PROPERTY_TYPES.map(type => (
                    <div
                      key={type.value}
                      className={`fa-type-card ${formData.propertyType === type.value ? 'fa-type-card--selected' : ''}`}
                      onClick={() => updateField('propertyType', type.value)}
                    >
                      <PropertyIcon type={type.value} size={20} />
                      <div className="fa-type-label">{t('findAgent.propertyTypes.' + type.tKey)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="fa-row">
              <div className="fa-section fa-section--half">
                <label className="fa-label">{t('findAgent.cityLabel')}</label>
                <select
                  className="fa-select"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                >
                  <option value="">{t('findAgent.selectCity')}</option>
                  {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="fa-section fa-section--half">
                <label className="fa-label">{t('findAgent.districtLabel')} <span className="fa-optional">{t('findAgent.optional')}</span></label>
                <input
                  type="text"
                  className="fa-input"
                  placeholder={t('findAgent.districtPlaceholder')}
                  value={formData.district}
                  onChange={(e) => updateField('district', e.target.value)}
                />
              </div>
            </div>

            {showBedrooms && (
              <div className="fa-section">
                <label className="fa-label">{t('findAgent.bedroomsLabel')} <span className="fa-optional">{t('findAgent.optional')}</span></label>
                <div className="fa-pill-group">
                  {BEDROOMS.map(br => (
                    <button
                      key={br}
                      type="button"
                      className={`fa-pill ${formData.bedrooms === br ? 'fa-pill--selected' : ''}`}
                      onClick={() => updateField('bedrooms', br)}
                    >
                      {br}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showBudget && (
              <div className="fa-section">
                <label className="fa-label">
                  {formData.lookingFor === 'rent' ? t('findAgent.monthlyBudget') : t('findAgent.budgetRange')}
                </label>
                <div className="fa-row">
                  <input
                    type="number"
                    className="fa-input"
                    placeholder={t('findAgent.fromPlaceholder')}
                    value={formData.budgetMin}
                    onChange={(e) => updateField('budgetMin', e.target.value)}
                  />
                  <input
                    type="number"
                    className="fa-input"
                    placeholder={t('findAgent.toPlaceholder')}
                    value={formData.budgetMax}
                    onChange={(e) => updateField('budgetMax', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="fa-step">
            <h1 className="fa-headline">
              {isSellerOrLandlord ? t('findAgent.step3TitleSell') : t('findAgent.step3TitleBuy')}
            </h1>
            <p className="fa-subline">
              {isSellerOrLandlord ? t('findAgent.step3SubSell') : t('findAgent.step3SubBuy')}
            </p>

            <div className="fa-section">
              <label className="fa-label">{t('findAgent.situationLabel')}</label>
              <div className="fa-radio-stack">
                {currentSituations.map(s => {
                  const Icon = s.Icon;
                  return (
                    <label
                      key={s.value}
                      className={`fa-radio-card ${formData.situation === s.value ? 'fa-radio-card--selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="situation"
                        value={s.value}
                        checked={formData.situation === s.value}
                        onChange={(e) => updateField('situation', e.target.value)}
                      />
                      <span className="fa-radio-icon-wrap"><Icon size={18} /></span>
                      <span className="fa-radio-text">{t('findAgent.situations.' + formData.lookingFor + '.' + s.value)}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="fa-section">
              <label className="fa-label">
                {isSellerOrLandlord ? t('findAgent.timeframeSellLabel') : t('findAgent.timeframeBuyLabel')}
              </label>
              <div className="fa-pill-group fa-pill-group--wrap">
                {(isSellerOrLandlord ? TIMEFRAMES_SELL : MOVE_IN_OPTIONS).map(optValue => {
                  const tKey = isSellerOrLandlord
                    ? 'findAgent.timeframesSell.' + optValue
                    : 'findAgent.moveIn.' + optValue;
                  const selected = (isSellerOrLandlord ? formData.sellingTimeframe : formData.moveInTiming) === optValue;
                  return (
                    <button
                      key={optValue}
                      type="button"
                      className={`fa-pill ${selected ? 'fa-pill--selected' : ''}`}
                      onClick={() => isSellerOrLandlord
                        ? updateField('sellingTimeframe', optValue)
                        : updateField('moveInTiming', optValue)
                      }
                    >
                      {t(tKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="fa-step">
            <h1 className="fa-headline">{t('findAgent.step4Title')}</h1>
            <p className="fa-subline">{t('findAgent.step4Sub')}</p>

            <div className="fa-radio-stack">
              {currentPriorities.map(p => {
                const Icon = p.Icon;
                return (
                  <label
                    key={p.value}
                    className={`fa-radio-card ${formData.priority === p.value ? 'fa-radio-card--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="priority"
                      value={p.value}
                      checked={formData.priority === p.value}
                      onChange={(e) => updateField('priority', e.target.value)}
                    />
                    <span className="fa-radio-icon-wrap"><Icon size={18} /></span>
                    <span className="fa-radio-text">{t('findAgent.priorities.' + formData.lookingFor + '.' + p.value)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="fa-step">
            <h1 className="fa-headline">{t('findAgent.step5Title')}</h1>
            <p className="fa-subline">{t('findAgent.step5Sub')}</p>

            <div className="fa-section">
              <label className="fa-label">{t('findAgent.fullNameLabel')}</label>
              <input
                type="text"
                className="fa-input"
                placeholder={t('findAgent.fullNamePlaceholder')}
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
              />
            </div>

            <div className="fa-row">
              <div className="fa-section fa-section--half">
                <label className="fa-label">{t('findAgent.emailLabel')}</label>
                <input
                  type="email"
                  className="fa-input"
                  placeholder={t('findAgent.emailPlaceholder')}
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
              </div>
              <div className="fa-section fa-section--half">
                <label className="fa-label">{t('findAgent.phoneLabel')} <span className="fa-optional">{t('findAgent.optional')}</span></label>
                <input
                  type="tel"
                  className="fa-input"
                  placeholder={t('findAgent.phonePlaceholder')}
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
            </div>

            <div className="fa-section">
              <label className="fa-label">{t('findAgent.contactMethodLabel')}</label>
              <div className="fa-pill-group fa-pill-group--equal-3">
                {CONTACT_METHODS.map(m => (
                  <button
                    key={m}
                    type="button"
                    className={`fa-pill ${formData.preferredContactMethod === m ? 'fa-pill--selected' : ''}`}
                    onClick={() => updateField('preferredContactMethod', m)}
                  >
                    {t('findAgent.contactMethods.' + m)}
                  </button>
                ))}
              </div>
            </div>

            <label className="fa-consent">
              <input
                type="checkbox"
                checked={formData.consent}
                onChange={(e) => updateField('consent', e.target.checked)}
              />
              <span className="fa-consent-text">
                {t('findAgent.consentLabel')}
              </span>
            </label>

            {error && <div className="fa-error">{error}</div>}
          </div>
        )}

        <div className="fa-actions">
          {step > 1 ? (
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              onClick={handleBack}
              disabled={submitting}
            >
              {t('findAgent.back')}
            </button>
          ) : <span />}

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              {t('findAgent.continue')}
            </button>
          ) : (
            <button
              type="button"
              className="fa-btn fa-btn--primary fa-btn--large"
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
            >
              {submitting ? t('findAgent.finding') : t('findAgent.findMyAgents')}
            </button>
          )}
        </div>

        <p className="findAgent-apply-link">
          Are you an agent?
          <button type="button" onClick={() => setShowApplyModal(true)}>
            Apply to join →
          </button>
        </p>
      </div>

      <BrokerApplicationModal open={showApplyModal} onClose={() => setShowApplyModal(false)} />
    </div>
  );
}
